import { seriesTypes } from './highcharts.constants';
import type HighchartsNamespace from 'highcharts';
import type {
  HighchartsAxisWithBreaks,
  HighchartsAxisWithLogarithmic,
  HighchartsChartWithRenderTo,
  HighchartsChartWithRendererUrl,
  HighchartsPointWithDlBox,
  HighchartsTooltipWithLabel,
} from './highcharts.types';

export type Highcharts = typeof HighchartsNamespace;
export type GraphPathFunction = (this: Highcharts.Series, points: Highcharts.Point[]) => Highcharts.SVGPathArray;
type PathSegment = Highcharts.SVGPathArray[number];
type DisplayErrorEvent = {
  code?: number;
  preventDefault: () => void;
};
type AxisLinePathProceed = (lineWidth: number) => Highcharts.SVGPathArray;
type TooltipRefreshProceed = (point: unknown, mouseEvents: unknown) => void;
type AlignDataLabelFunction = (
  this: Highcharts.Series,
  point: Highcharts.Point,
  dataLabel: Highcharts.SVGElement,
  options: Highcharts.DataLabelsOptions,
  ...rest: unknown[]
) => unknown;

/**
 * Returns whether the current browser runtime reports itself as Firefox.
 */
export function browserIsFirefox() {
  return typeof navigator !== 'undefined' && /firefox/i.test(navigator.userAgent);
}

/**
 * Applies the Seeq minimum-pixel-spacing adjustment to the line-series graph
 * path generated by Highcharts' base line prototype.
 */
export function createMinPixelSpacingGraphPath(
  this: Highcharts.Series,
  points: Highcharts.Point[],
  originalGetGraphPath: GraphPathFunction = seriesTypes.line.prototype.getGraphPath,
) {
  const path = originalGetGraphPath.call(this, points);
  if (!path || path.length === 0) {
    return path;
  }

  const adjustedPath: Highcharts.SVGPathArray = [];
  let prevSegment: PathSegment | undefined;

  for (const segment of path) {
    let nextSegment = segment;
    if (segment && ['M', 'L'].includes(segment[0]) && prevSegment && ['M', 'L'].includes(prevSegment[0])) {
      const [command, x, y] = segment;
      const [, prevX, prevY] = prevSegment;

      if (typeof x === 'number' && typeof y === 'number' && typeof prevX === 'number' && typeof prevY === 'number') {
        const dx = x - prevX;
        const dy = y - prevY;
        const distance = Math.sqrt(dx * dx + dy * dy);

        if (distance < 1 && distance > 0) {
          const scale = 1 / distance;
          const newX = prevX + dx * scale;
          const newY = prevY + dy * scale;
          nextSegment = [command, newX, newY];
        }
      }
    }

    adjustedPath.push(nextSegment);
    prevSegment = nextSegment;
  }

  return adjustedPath;
}

/**
 * Adds slash markers to horizontal broken axes by inserting the extra path
 * segments directly into Highcharts' computed axis line path.
 */
export function getBrokenAxisLinePath(this: HighchartsAxisWithBreaks, proceed: AxisLinePathProceed, lineWidth: number) {
  const path = proceed.call(this, lineWidth);
  const start = path[0];
  const y = start[2];
  if (typeof y !== 'number') {
    return path;
  }

  (this.brokenAxis?.breakArray || []).forEach((brk) => {
    if (this.horiz) {
      const x = this.toPixels(brk.from);
      path.splice(
        3,
        0,
        ['L', x - 4, y],
        ['M', x - 9, y + 5],
        ['L', x + 1, y - 5],
        ['M', x - 1, y + 5],
        ['L', x + 9, y - 5],
        ['M', x + 4, y],
      );
    }
  });

  return path;
}

/**
 * Recolors the tooltip label to match the hovered series when the tooltip
 * background is configured with the Seeq-specific `series` sentinel value.
 */
export function refreshTooltipWithSeriesBackground(
  this: HighchartsTooltipWithLabel,
  proceed: TooltipRefreshProceed,
  point: Highcharts.Point | undefined,
  mouseEvents: unknown,
) {
  proceed.call(this, point, mouseEvents);

  const label = this.label;
  if (this.options.backgroundColor === 'series' && point && label) {
    label.attr({
      fill: point.series.color,
    });
  }
}

/**
 * Enables Seeq's negative-logarithm axis behavior by swapping Highcharts'
 * default positive-only transforms for symmetric negative/positive mappings.
 */
export function configureNegativeLogAxis(this: HighchartsAxisWithLogarithmic) {
  const logarithmic = this.logarithmic;
  if (logarithmic && this.options.custom?.allowNegativeLog) {
    this.positiveValuesOnly = false;

    logarithmic.log2lin = (num: number) => {
      const isNegative = num < 0;

      let adjustedNum = Math.abs(num);
      if (adjustedNum < 10) {
        adjustedNum += (10 - adjustedNum) / 10;
      }

      const result = Math.log(adjustedNum) / Math.LN10;
      return isNegative ? -result : result;
    };

    logarithmic.lin2log = (num: number) => {
      const isNegative = num < 0;

      let result = Math.pow(10, Math.abs(num));
      if (result < 10) {
        result = (10 * (result - 1)) / (10 - 1);
      }
      return isNegative ? -result : result;
    };
  }
}

/**
 * Suppresses Highcharts error 12, which is expected in some Seeq chart flows
 * and should not interrupt rendering.
 */
export function suppressDisplayError12(event: DisplayErrorEvent) {
  if (event.code === 12) {
    event.preventDefault();
  }
}

/**
 * Rewrites cached clip-path URLs before redraw so browser URL changes do not
 * break Heatmap clipping, with Firefox-specific base-tag handling.
 */
export function createBeforeRedrawHandler(this: HighchartsChartWithRendererUrl) {
  if (this.renderer.url !== window.location.href) {
    const elements = this.container.querySelectorAll(`[clip-path*="${this.renderer.url}"]`);

    elements.forEach((element: Element): void => {
      const currentValue = element.getAttribute('clip-path');
      if (currentValue) {
        element.setAttribute('clip-path', currentValue.replace(this.renderer.url, window.location.href));
      }
    });

    this.renderer.url =
      browserIsFirefox() && document.getElementsByTagName('base').length
        ? window.location.href
            .split('#')[0]
            .replace(/<[^>]*>/g, '')
            .replace(/([()'])/g, '\\$1')
            .replace(/ /g, '%20')
        : '';
  }
}

/**
 * Applies ellipsis width constraints to flame-series data labels before
 * delegating to Highcharts' base column alignment logic.
 */
export function createFlameAlignDataLabel(baseAlignDataLabel: AlignDataLabelFunction) {
  return function (
    this: Highcharts.Series,
    point: HighchartsPointWithDlBox,
    dataLabel: Highcharts.SVGElement,
    options: Highcharts.DataLabelsOptions,
    ...rest: unknown[]
  ) {
    if (point && dataLabel && options.style?.textOverflow === 'ellipsis') {
      const dlBox = point.dlBox || point.shapeArgs;
      if (dlBox) {
        dataLabel.css({
          width: this.chart.inverted ? dlBox.height : dlBox.width,
          textOverflow: options.style.textOverflow,
        });
      }
    }

    return baseAlignDataLabel.call(this, point, dataLabel, options, ...rest);
  };
}

/**
 * Restores the legacy jQuery-style `renderTo.highcharts()` accessor expected
 * by older Seeq code after Highcharts creates the chart container.
 */
export function shimHighchartsGetContainer(this: HighchartsChartWithRenderTo, proceed: () => void) {
  proceed.apply(this);
  this.renderTo.highcharts = () => this;
}
